import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Layers,
  Building2,
  MapPin,
  ExternalLink,
  Users,
  Search,
  Sparkles,
} from 'lucide-react';
import { useProjects, type ProjectRow } from '../lib/queries';
import Shell from '../components/Shell';
import ProjectDrawingsLedger from '../components/ProjectDrawingsLedger';
import { Select, StatusChip, EmptyState, TableSkeleton, ErrorState } from '../components/ui';

export default function ProjectDrawings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: projects, isLoading, error, refetch } = useProjects('', '');

  const paramProjectId = searchParams.get('projectId') || '';
  const [selectedProjectId, setSelectedProjectId] = useState<string>(paramProjectId);

  // Sync state if URL param changes or default to first project
  useEffect(() => {
    if (paramProjectId) {
      setSelectedProjectId(paramProjectId);
    } else if (projects && projects.length > 0 && !selectedProjectId) {
      if (projects[0]?.id) {
        setSelectedProjectId(projects[0].id);
      }
    }
  }, [paramProjectId, projects, selectedProjectId]);

  const selectedProject = projects?.find((p) => p.id === selectedProjectId) ?? projects?.[0];

  function handleSelectProject(id: string) {
    setSelectedProjectId(id);
    setSearchParams(id ? { projectId: id } : {});
  }

  const projectOptions = (projects ?? []).map((p) => ({
    value: p.id,
    label: `${p.name} (${p.clientName})`,
  }));

  return (
    <Shell
      title="Project Drawings"
      crumb="Overview"
      actions={
        selectedProject && (
          <button
            className="btn btn-auto btn-sm"
            onClick={() => navigate(`/projects/${selectedProject.id}`)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            title="Open project workspace"
          >
            <Building2 size={13} />
            <span className="btn-label-desktop">Open Project Workspace</span>
            <span className="btn-label-mobile">Workspace</span>
            <ExternalLink size={12} style={{ color: 'var(--text-faint)' }} />
          </button>
        )
      }
    >
      {/* Project Selector Toolbar */}
      <div className="toolbar project-drawings-toolbar">
        <div className="pd-selector-wrap">
          <Layers size={18} style={{ color: 'var(--accent)', flexShrink: 0 }} />
          <span className="pd-selector-label">
            Select Project:
          </span>
          <div className="pd-selector-select">
            <Select
              value={selectedProject?.id ?? ''}
              onChange={handleSelectProject}
              options={projectOptions}
              placeholder="Choose a project to view drawings…"
            />
          </div>
        </div>

        {selectedProject && (
          <div className="pd-project-meta">
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Users size={13} style={{ color: 'var(--text-faint)' }} />
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{selectedProject.clientName}</span>
            </div>
            {selectedProject.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <MapPin size={13} style={{ color: 'var(--text-faint)' }} />
                <span>{selectedProject.location}</span>
              </div>
            )}
            <StatusChip value={selectedProject.status} />
          </div>
        )}
      </div>

      {/* Main Drawing Area */}
      {isLoading ? (
        <div className="card" style={{ padding: 24 }}>
          <TableSkeleton rows={4} cols={4} />
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 24 }}>
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : !projects?.length ? (
        <div className="card" style={{ padding: 40 }}>
          <EmptyState
            icon="briefcase"
            title="No projects registered yet"
            hint="Create a client and project first to upload and manage architectural drawings."
          />
        </div>
      ) : selectedProject ? (
        <motion.div
          key={selectedProject.id}
          className="card"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          style={{ overflow: 'hidden' }}
        >
          <ProjectDrawingsLedger
            projectId={selectedProject.id}
            projectName={selectedProject.name}
          />
        </motion.div>
      ) : null}
    </Shell>
  );
}
